import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Injectable } from '@angular/core';
import { AppStateService } from '@libs/shared/common';
import { PageStateModel } from '@libs/shared/models';
import { combineLatest, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { AuthDebuggerService } from '../auth';
import { DebuggerPageState } from '../debugger.model';
import { OptionsService } from '../services';
import { bffEnvMap } from './page-debugger.model';

@Injectable({ providedIn: 'root' })
export class PageDebuggerService {
  private readonly pageId$: Observable<string>;
  private readonly customerNumber$: Observable<string>;
  private readonly bffEnv$: Observable<string>;
  private readonly showNav$: Observable<boolean>;
  private readonly page$: Observable<PageStateModel>;

  public constructor(
    private readonly options: OptionsService,
    private readonly authDebugger: AuthDebuggerService,
    private readonly appState: AppStateService,
  ) {
    this.pageId$ = this.options.get('pageId');
    this.customerNumber$ = this.options.get('customerNumber');

    this.bffEnv$ = this.options.get('bffEnv').pipe(
      tap(bffEnv => void this.appState.set('bffUrl', bffEnvMap[bffEnv])),
      shareReplay(1),
    );

    this.page$ = combineLatest([this.pageId$, this.customerNumber$]).pipe(
      switchMap(([rootId, customerNumber]) => this.authDebugger.waitUpdated().pipe(
        map(() => ({ rootId, customerNumber })),
        tap(page => void this.appState.set('page', page)),
      )),
      shareReplay(1),
    );

    this.showNav$ = this.options.get('showNav').pipe(
      map<string, boolean>(coerceBooleanProperty),
      tap(showNav => void this.appState.set('hidePageNavigation', !showNav)),
    );
  }

  public getPageState(): Observable<DebuggerPageState> {
    return combineLatest({
      bffEnv: this.bffEnv$,
      pageId: this.pageId$,
      showNav: this.showNav$,
      page: this.page$,
    });
  }

  public updatePageState(state: Omit<DebuggerPageState, 'page'>): Observable<void> {
    return of(undefined).pipe(
      tap(() => void this.options.update({
        bffEnv: state.bffEnv,
        pageId: state.pageId,
        showNav: String(state.showNav),
      })),
      switchMap(() => this.page$),
      map(() => void {}),
    );
  }

  public updateNavigationVisibility(showNav: boolean): void {
    this.options.update({ showNav: String(showNav) });
  }
}
